// src/utils/ensureCategories.js
import { fetchData } from "../api/client";

const strip = (s) =>
  String(s || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

const keyOf = (c, s) => `${String(c || "").trim()}|${String(s || "").trim()}`;

/**
 * Recebe pares { categoria, subcategoria } (por nome),
 * procura nos endpoints de categorias e cria o que faltar.
 * Retorna um Map key-> { id_categoria, id_subcategoria }.
 */
export async function ensureCategoriesByNameBatch(pairs) {
  // 1) filtra/deduplica
  const unique = [];
  const seen = new Set();
  for (const p of pairs || []) {
    if (!p) continue;
    const cat = (p.categoria || "").trim();
    const sub = (p.subcategoria || "").trim();
    if (!cat && !sub) continue;
    const k = keyOf(cat, sub);
    if (!seen.has(k)) {
      seen.add(k);
      unique.push({ categoria: cat, subcategoria: sub });
    }
  }
  const result = new Map();
  if (!unique.length) return result;

  // 2) carrega todas para busca por nome (case/acento-insensitive)
  const listResp = await fetchData(`/categories?page=1&limit=10000`);
  const flat = Array.isArray(listResp?.data) ? listResp.data : [];
  const byName = new Map(flat.map((c) => [strip(c.nome), c]));

  // cache local de subcategorias por pai
  const subsCache = new Map();
  async function getSubs(parentId) {
    if (subsCache.has(parentId)) return subsCache.get(parentId);
    const res = await fetchData(`/categories?parentId=${parentId}`, "GET");
    const arr = Array.isArray(res?.data) ? res.data : [];
    subsCache.set(parentId, arr);
    return arr;
  }

  // 3) resolve/cria
  for (const { categoria, subcategoria } of unique) {
    let idCat = null;
    let idSub = null;

    if (categoria) {
      const hit = byName.get(strip(categoria));
      if (hit) {
        idCat = Number(hit.id);
      } else {
        const created = await fetchData(`/categories`, "POST", {
          nome: categoria,
          status: 1,
          id_pai: 0,
        });
        idCat = Number(created?.data?.id || created?.id);
        // mantém índice local consistente
        byName.set(strip(categoria), { id: idCat, nome: categoria, id_pai: 0 });
      }
    }

    if (subcategoria && idCat) {
      const subs = await getSubs(idCat);
      const got = subs.find((s) => strip(s.nome) === strip(subcategoria));
      if (got) {
        idSub = Number(got.id);
      } else {
        const created = await fetchData(`/categories`, "POST", {
          nome: subcategoria,
          status: 1,
          id_pai: idCat,
        });
        idSub = Number(created?.data?.id || created?.id);
        subsCache.set(idCat, [
          ...subs,
          { id: idSub, nome: subcategoria, id_pai: idCat, status: 1 },
        ]);
      }
    }

    result.set(keyOf(categoria, subcategoria), {
      id_categoria: idCat,
      id_subcategoria: idSub,
    });
  }

  return result;
}
